import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, Lightning, CircleNotch, CheckCircle, XCircle, Play,
  Envelope, Target, Lightbulb, ArrowRight
} from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function JobStatusIcon({ status }) {
  switch (status) {
    case 'running': return <CircleNotch size={16} weight="bold" className="text-[#0055FF] animate-spin" />;
    case 'completed': return <CheckCircle size={16} weight="fill" className="text-emerald-600" />;
    case 'failed': return <XCircle size={16} weight="fill" className="text-red-500" />;
    default: return <CircleNotch size={16} className="text-zinc-400" />;
  }
}

export default function EnrichmentPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [starting, setStarting] = useState(false);

  const fetchData = () => {
    Promise.all([
      fetch(`${API}/enrich/stats`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/enrich/jobs?limit=30`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([s, j]) => {
      setStats(s);
      setJobs(j.jobs || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 4000);
    return () => clearInterval(iv);
  }, []);

  const startBulkEnrich = async () => {
    if (!selectedCountry) return;
    setStarting(true);
    try {
      const res = await fetch(`${API}/enrich/bulk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country_code: selectedCountry })
      });
      const data = await res.json();
      if (data.jobs?.length > 0) {
        fetchData();
      }
    } catch { /* ignore */ }
    setStarting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="enrichment-page">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Brain size={28} weight="bold" className="text-[#0055FF]" />
          <h1 className="font-heading text-3xl font-black tracking-tight">AI Enrichment</h1>
        </div>
        <p className="text-sm text-zinc-500 mt-1">
          Powered by GPT-5.2 — Infer email patterns, score contacts, and generate commercial insights
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="enrichment-stats">
        {[
          { label: 'Enriched Facilities', value: `${stats?.enriched_facilities || 0} / ${stats?.total_facilities || 0}`, icon: Lightning, color: 'text-[#0055FF]' },
          { label: 'Inferred Emails', value: stats?.contacts_with_inferred_email || 0, icon: Envelope, color: 'text-amber-600' },
          { label: 'Contacts Scored', value: stats?.contacts_scored || 0, icon: Target, color: 'text-emerald-600' },
          { label: 'Running Jobs', value: stats?.running_jobs || 0, icon: CircleNotch, color: 'text-[#0055FF]' },
        ].map((s, i) => (
          <div key={i} className="bg-white p-4">
            <div className="flex items-center gap-2 mb-2">
              <s.icon size={16} weight="bold" className={s.color} />
              <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{s.label}</span>
            </div>
            <div className="font-data text-2xl font-bold">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Bulk Enrich Panel */}
      <div className="border border-zinc-200 bg-white p-5 mb-6" data-testid="bulk-enrich-panel">
        <div className="flex items-center gap-2 mb-1">
          <Lightning size={20} weight="bold" className="text-[#0055FF]" />
          <h3 className="font-heading text-lg font-semibold">Bulk Enrichment</h3>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Enrich all unenriched facilities in a country using GPT-5.2. This will infer email patterns, generate probable email addresses, score contacts, and generate commercial insights.
        </p>
        <div className="flex items-center gap-3">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger data-testid="enrich-country-select" className="w-48 rounded-sm text-sm">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NL">Netherlands</SelectItem>
              <SelectItem value="BE">Belgium</SelectItem>
              <SelectItem value="FR">France</SelectItem>
              <SelectItem value="DE">Germany</SelectItem>
            </SelectContent>
          </Select>
          <button
            data-testid="start-bulk-enrich-btn"
            onClick={startBulkEnrich}
            disabled={!selectedCountry || starting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Brain size={16} weight="bold" />
            {starting ? 'Starting...' : 'Start AI Enrichment'}
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="border border-zinc-200 bg-white p-5 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-3">How AI Enrichment Works</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { step: '1', title: 'Email Pattern Detection', desc: 'Analyzes verified emails to detect the organization\'s email naming convention', icon: Envelope },
            { step: '2', title: 'Email Generation', desc: 'Generates probable email addresses for contacts without verified emails', icon: Lightning },
            { step: '3', title: 'Contact Scoring', desc: 'Scores each contact for commercial relevance and buying influence', icon: Target },
            { step: '4', title: 'Facility Insights', desc: 'Generates sales insights, budget estimates, and approach recommendations', icon: Lightbulb },
          ].map(s => (
            <div key={s.step} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-[#0055FF] text-white flex items-center justify-center text-sm font-bold shrink-0">
                {s.step}
              </div>
              <div>
                <div className="text-sm font-medium mb-0.5">{s.title}</div>
                <div className="text-xs text-zinc-500 leading-relaxed">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Jobs Table */}
      <div className="border border-zinc-200 overflow-hidden" data-testid="enrichment-jobs-table">
        <div className="bg-white px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">Enrichment Jobs</h3>
          <span className="text-xs text-zinc-500 font-data">{jobs.length} jobs</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Job</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Facility</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Status</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Progress</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Results</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Started</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-zinc-400">
                  No enrichment jobs yet. Start a bulk enrichment above or enrich individual facilities from their detail pages.
                </td>
              </tr>
            ) : (
              jobs.map(j => (
                <tr key={j.job_id} data-testid={`enrich-job-${j.job_id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <div className="font-data text-xs">{j.job_id}</div>
                    <Badge className="rounded-full px-2 py-0 text-[10px] font-data bg-zinc-100 text-zinc-600 mt-0.5">{j.country_code}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="font-medium text-sm cursor-pointer hover:text-[#0055FF]"
                      onClick={() => navigate(`/facilities/${j.facility_id}`)}
                    >
                      {j.facility_name || j.facility_id}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <JobStatusIcon status={j.status} />
                      <span className="text-xs">{j.status}</span>
                    </div>
                    {j.current_step && j.status === 'running' && (
                      <div className="text-xs text-zinc-400 mt-0.5">{j.current_step}</div>
                    )}
                    {j.error && (
                      <div className="text-xs text-red-500 mt-0.5">{j.error}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="w-24">
                      <Progress value={j.progress || 0} className="h-2" />
                      <span className="font-data text-xs text-zinc-500">{j.progress || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {j.results ? (
                      <div className="text-xs space-y-0.5">
                        {j.results.emails_generated > 0 && (
                          <div className="flex items-center gap-1">
                            <Envelope size={12} className="text-amber-500" />
                            <span>{j.results.emails_generated} emails inferred</span>
                          </div>
                        )}
                        {j.results.contacts_scored > 0 && (
                          <div className="flex items-center gap-1">
                            <Target size={12} className="text-emerald-500" />
                            <span>{j.results.contacts_scored} contacts scored</span>
                          </div>
                        )}
                        {j.results.has_insights && (
                          <div className="flex items-center gap-1">
                            <Lightbulb size={12} className="text-[#0055FF]" />
                            <span>Insights generated</span>
                          </div>
                        )}
                        {j.results.email_pattern && (
                          <div className="font-data text-[10px] text-zinc-400 mt-0.5">
                            Pattern: {j.results.email_pattern}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-data text-xs">{j.started_at ? new Date(j.started_at).toLocaleString() : '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
